// © Microsoft Corporation. All rights reserved.

import { IsForceTimestampExpired, forceStorageTimestampKey } from "./PersistHelpers";
import { RenderCacheErrors, logger } from "@msnews/diagnostics";
import { appEnvironment, localStorage } from "@msnews/core";
import { batchCSS, scrapeDom } from "./Helper";
import { defaultExpirationTimeMs, expirationPrefix, jssMapKey, renderCacheExpirationKey, renderCacheKey, storagePrefix } from "./Constants";

import { RenderCache } from "./RenderCache";
import { RenderCacheConfig } from ".";
import { track } from "@msnews/telemetry-transitional";
import { userInteractionHappened } from "./UserInteractionWatcher";

let JSSMap: { [instanceId: string]: string } = {};
let jssCheckCompleted = false;
let jssErrorMessage = "";

/**
 * Persist the complete render state (DOM and Styles) necessary to restore the rendered Peregrine SPA page, without needing to
 * first load all of the Peregrine experience bundles.
 * This is the equivalent of using Server Side Rendering to generate the initial DOM, though in this case the initial DOM elements and styles
 * are persisted and loaded locally on the client.
 * @param config Render cache configuration.
 * @param stateJson The state to persist in JSON form.
 * @param markers List of performance markers, which will be fired upon cache restore.
 */
export function persistRenderCache(config: RenderCacheConfig, stateJson?: string, markers?: string[]): void {
    jssCheckCompleted = true;
    jssErrorMessage = "";

    if (!config || !localStorage.supported) {
        return;
    }

    // Check if we need to refresh cache by force. Used as Failsafe if user stuck in broken render cache loop.
    if (IsForceTimestampExpired()) {
        removeRenderCache();
        return;
    }

    // Found an issue with User interaction check. Its inconsistent and as we are not deleting RC on this,
    // users may get stuck in this state.
    if (userInteractionHappened()) {
        return;
    }

    const persist = compareJssMap();

    let { includeNewsFeed, expirationTime, newsFeedExpirationTime  } = config;

    // Persist build version recorded for the page being render cached
    const buildVersion = appEnvironment.BuildVersion || "";

    // Form Cache Object
    const renderCacheStorageObj: RenderCache = {
        dom: scrapeDom(includeNewsFeed),
        batch_css: batchCSS(),
        build_version: buildVersion,
        markers,
        state: stateJson
    };

    expirationTime = (includeNewsFeed ? newsFeedExpirationTime : expirationTime)
                             || defaultExpirationTimeMs;

    // Try to persist Render Cache and Timestamp
    const expiresOn: number = new Date().getTime() + expirationTime;

    localStorage.setObject(renderCacheExpirationKey, expiresOn);
    localStorage.setObject(renderCacheKey, renderCacheStorageObj);

    if (!persist) {
        track.trackAppErrorEvent({
            ...RenderCacheErrors.JssStyleMismatchError,
            message: "RC JSS mismatch issue",
            pb: { customMessage: jssErrorMessage }
        });
    } else {
        Object.keys(JSSMap).length && localStorage.setObject(jssMapKey, JSSMap);
        logger.log("[RC Info] No style mismatch found");
    }
}

/**
 * Deletes render cache local storage
 */
export function removeRenderCache(): void {
    if (!localStorage.supported) {
        return;
    }

    localStorage.removeSubstringKeys(forceStorageTimestampKey);
    localStorage.removeSubstringKeys(storagePrefix);
    localStorage.removeSubstringKeys(expirationPrefix);
}

/**
 * Maintains Dictionary of class names
 * @param key key for class name
 * @param val JSS val for class name
 */
export function jssClassGeneratorHandler(key: string, val: string): void {
    if (jssCheckCompleted || !(window && window.renderCacheEnabled)) {
        return;
    }

    if (!JSSMap[key]) {
        JSSMap[key] = val;
    }
}

/**
 * Compares stored JSS Map with current JSS Map.
 * @returns boolean - returns false when there is mismatach, otherwise true
 */
export function compareJssMap(): boolean {
    let jssIdentical = true;
    let storedMap = localStorage.getObject(jssMapKey);

    if (storedMap && typeof storedMap === "object" && Object.keys(storedMap).length) {
        // this for loop compares every item in stored Map of Css class names to the current page instance map
        for (const key in storedMap) {
            const val = storedMap[key];
            const jssMapVal = JSSMap[key];
            if (jssMapVal && jssMapVal !== val) {
                jssErrorMessage += `JSSError: val mismatch for key: ${key}, val stored: ${val}, new val: ${jssMapVal}`;
                jssIdentical = false;
            }
        }
    }

    return jssIdentical;
}